// https://medium.com/@FezVrasta/service-worker-updates-and-error-handling-with-react-1a3730800e6a
import { supportsPassiveEvents } from 'detect-it';
import React, { useState } from 'react';
import { isSwReloadHeld } from './swReloadHold';

const ServiceWorkerContext = React.createContext();

export const ServiceWorkerProvider = ({ children }) => {
  const [isUpdateAvailable, setUpdateAvailable] = useState(false);

  React.useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      window.workbox !== undefined
    ) {
      const wb = window.workbox;

      const promptNewVersionAvailable = () => {
        setUpdateAvailable(true);
      };

      wb.addEventListener('installed', event => {
        if (event.isUpdate) {
          promptNewVersionAvailable();
        }
      });

      // When a new SW takes control after a deploy, reload so we serve fresh
      // HTML + chunks. workbox fires 'controlling' on the very first install
      // too (clientsClaim) and when another tab's SW takes over (isExternal) —
      // reloading then would yank the page away from first-time visitors.
      // The post editor holds this reload off (swReloadHold): a deploy landing
      // while an author is writing must not take the page away from them.
      wb.addEventListener('controlling', event => {
        if (event.isUpdate && !event.isExternal && !isSwReloadHeld())
          window.location.reload();
      });

      wb.addEventListener(
        'pushsubscriptionchange',
        event => {
          console.log('Subscription expired');
          event.waitUntil(
            navigator.serviceWorker.ready.then(reg => {
              reg.pushManager.getSubscription().then(sub => {
                if (
                  sub &&
                  !(
                    sub.expirationTime &&
                    Date.now() > sub.expirationTime - 5 * 60 * 1000
                  )
                ) {
                  const variables = {
                    pushSubscription: JSON.stringify(sub),
                  };
                  // Imported at call time: this provider wraps every page via
                  // _app, and a static import here would drag graphql-tag +
                  // graphql-request (~17KB gz) into the shared bundle for an
                  // event that fires only when a push subscription expires.
                  Promise.all([
                    import('./graphql/notifications'),
                    import('./graphQLClient'),
                  ]).then(
                    ([{ ADD_PUSH_SUBSCRIPTION }, { default: graphQLClient }]) =>
                      graphQLClient(ADD_PUSH_SUBSCRIPTION, variables).then(() =>
                        console.log(
                          'Submitted push notification subscription to API',
                        ),
                      ),
                  );
                }
              });
            }),
          );
        },
        supportsPassiveEvents ? { passive: true } : false,
      );

      wb.register();
    }
  }, []);

  const value = React.useMemo(
    () => ({
      isUpdateAvailable,
      updateAssets: () => {
        window.location.reload();
      },
    }),
    [isUpdateAvailable],
  );

  return (
    <ServiceWorkerContext.Provider value={value}>
      {children}
    </ServiceWorkerContext.Provider>
  );
};

// With this React Hook we'll be able to access `isUpdateAvailable` and `updateAssets`
export const useServiceWorker = () => {
  return React.useContext(ServiceWorkerContext);
};
